import { useState } from 'react'

const baseColors = [
  'black',
  'gray',
  'white',
  'primary',
  'secondary',
  'teal',
  'blue',
  'orange',
  'red',
  'green',
] as const

const lightVariants = [10, 20, 40, 60, 80] as const

type BaseColor = typeof baseColors[number]
type LightVariant = typeof lightVariants[number]

type ColorKey =
  | BaseColor
  | `${BaseColor}-light-${LightVariant}`

export const colors: Record<ColorKey, string> = Object.fromEntries([
  ...baseColors.map(color => [
    color,
    `var(--color-${color})`,
  ]),
  ...baseColors.flatMap(color =>
    lightVariants.map(v => [
      `${color}-light-${v}`,
      `var(--color-${color}-light-${v})`,
    ]),
  ),
]) as Record<ColorKey, string>

export type ColorVariant = keyof typeof colors


/*---------- Theme Management ----------*/

const THEME_CLASS = 'dark'
const THEME_STORAGE_KEY = 'theme'

const toggleTheme = () => {
  const root = document.documentElement
  const isDark = root.classList.toggle(THEME_CLASS)

  localStorage.setItem(THEME_STORAGE_KEY, isDark ? 'dark' : 'light')
}

const getActiveTheme = (): 'dark' | 'light' =>
(document.documentElement.classList.contains(THEME_CLASS)
  ? 'dark'
  : 'light')

const applyInitialTheme = () => {
  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY)

  if (savedTheme === 'dark') {
    document.documentElement.classList.add(THEME_CLASS)
    return
  }

  if (savedTheme === 'light') {
    document.documentElement.classList.remove(THEME_CLASS)
    return
  }

  const prefersDark = window.matchMedia?.(
    '(prefers-color-scheme: dark)',
  ).matches

  document.documentElement.classList.toggle(THEME_CLASS, prefersDark)
}

applyInitialTheme()

export const useTheme = () => {
  const [theme, setTheme] = useState<'dark' | 'light'>(getActiveTheme)

  const toggle = () => {
    toggleTheme()
    setTheme(getActiveTheme())
  }

  return { theme, toggle }
}
